"use client";
import * as React from "react";
import * as yup from "yup";

import {
  Box,
  Button,
  Container,
  FormControl,
  FormHelperText,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  TextField,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { LocalizationProvider, MobileDatePicker } from "@mui/x-date-pickers";
import { useState } from "react";
import moment, { Moment } from "moment";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { subscribeToNewsLetter } from "@/services/backOffice";
import KeyboardDoubleArrowRightIcon from "@mui/icons-material/KeyboardDoubleArrowRight";
import { AxiosError } from "axios";
import OtpNewslater from "../home/OtpNewslater";
import CircularProgress from "@mui/material/CircularProgress";
import Image from "next/image";

interface MembersOnlytype {
  firstName: string;
  lastName: string;
  emailAddress: string;
  homeAirport?: string;
  favouriteDestination?: string;
  mobileNumber: string;
  zipCode?: number;
  smsAlert?: boolean;
  DOB: {
    loc: {
      locale?: string;
    };
  };
}

const membersOnlySchema = yup.object().shape({
  firstName: yup.string().required("First Name is Required"),
  lastName: yup.string().required("Last Name is Required"),
  emailAddress: yup.string().email("Must be a valid email").max(50).required("Email is Required"),
  homeAirport: yup.string(),
  favouriteDestination: yup.string(),
  mobileNumber: yup
    .string()
    .matches(
      /^((\\+[1-9]{1,4}[ \\-]*)|(\\([0-9]{2,3}\\)[ \\-]*)|([0-9]{2,4})[ \\-]*)*?[0-9]{3,4}?[ \\-]*[0-9]{3,4}?$/,
      "Phone number is not valid",
    )
    .required(),
  zipCode: yup.number(),
  smsAlert: yup.boolean(),
  DOB: yup
    .object({
      loc: yup.object({
        locale: yup.string(),
      }),
    })
    .nullable()
    .required("Date Of Birth is required"),
});

const MembersOnly = () => {
  const { handleSubmit, control, watch, reset } = useForm<MembersOnlytype>({
    defaultValues: {
      firstName: "",
      lastName: "",
      emailAddress: "",
      smsAlert: false,
    },
    resolver: yupResolver(membersOnlySchema),
    mode: "all",
  });

  const [isOtp, setIsOtp] = React.useState(false);

  const values = watch();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submitEnquiry = async () => {
    try {
      setLoading(true);
      await subscribeToNewsLetter({
        email: values.emailAddress,
        firstName: values.firstName,
        lastName: values.lastName,
        mobile: values.mobileNumber,
      });
      setIsOtp(true);
      setError(null);
      setLoading(false);
    } catch (err) {
      const error = err as AxiosError<{ detail?: string }>;
      if (error.response) {
        const errorMessage = error.response.data?.detail;
        setError(errorMessage || "An unexpected error occurred.");
      } else {
        setError("Something went wrong. Please try again.");
      }
      setLoading(false);
    }
  };

  const [dobDate, setDobDate] = useState<Moment | null>(null);

  const benefits = [
    "Free trial on the 1st year. (10 pounds annual fee from the 2nd year onwards)",
    "One Excursion from us. (Depending on the vibes we've shared, we will pick the perfect vibe for you)",
    "Flexible and easy payment methods.",
    "Pay in installments. Changes and cancellations. (Pay only supplier charges / 50 pounds admin fee per person) (Low-cost vibes 25 pounds)",
    "Special offers and deals.",
    "Discounts, Discount codes and vouchers will be released to VGUK members first.",
    "Fill your vibes, and we will fit your vibes (Birthdays/anniversaries). Get amazing surprises!",
    "The benefit of WhatsApp - Easy and fast news and deal alerts - hot offers.",
  ];
  return (
    <>
      <Box>
        <Box
          sx={{
            position: "relative",
            height: { xs: "15vh", sm: "20vh" },
            minHeight: { xs: "170px", md: "20rem" },
            width: "100%",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              zIndex: 2,
              top: {
                md: "calc(85px + 3rem)",
              },
              left: 0,
              right: 0,
              bottom: 0,
              height: "10rem",
            }}
          >
            <Container
              sx={{
                height: "100%",
                display: "flex",
                alignItems: "center",
                flexDirection: "column",
                justifyContent: "space-around",
                marginTop: "13px",
              }}
            >
              <Box>
                <Typography
                  variant="h2"
                  component={"h1"}
                  mb={0.5}
                  sx={{
                    color: "#fff",
                    textShadow: "0px 4px 4px rgba(0, 0, 0, 0.25), 0px 4px 20px rgba(0, 0, 0, 0.4)",
                    fontWeight: "bold",
                    fontSize: {
                      xs: "2rem",
                      sm: "2.6rem",
                      md: "2.8rem",
                      lg: "3rem",
                    },
                  }}
                >
                  Members Only
                </Typography>
              </Box>
            </Container>
          </Box>

          <Image
            src="/images/home/demo/cruise-line-bg.png"
            alt="members-only"
            fill
            style={{
              objectFit: "cover",
            }}
            quality={100}
            priority
          />
          <Box
            sx={{
              position: "absolute",
              zIndex: 1,
              top: "0px",
              left: "0px",
              width: "100%",
              height: "100%",
              bgcolor: "red",
              opacity: 0.2,
              backgroundColor: "rgb(98, 0, 255)",
              backgroundImage:
                "radial-gradient(at 11% 97%, hsla(322,0%,0%,1) 0px, transparent 50%), radial-gradient(at 29% 51%, hsla(283,0%,0%,1) 0px, transparent 50%)",
            }}
          ></Box>
          <Box
            sx={{
              position: "absolute",
              zIndex: 1,
              top: "0px",
              left: "0px",
              width: "100%",
              height: "100%",
              opacity: 0.4,
              backgroundColor: "rgb(0, 0, 0)",
            }}
          ></Box>
        </Box>
      </Box>
      <Container sx={{ mt: "2rem" }}>
        <Typography
          color="primary.main"
          variant="h4"
          component={"h2"}
          sx={{ marginBottom: "1rem" }}
        >
          VGUK Membership
        </Typography>
        <Typography variant="body1" component="p">
          <Box>
            World Cruise Vibes is one of the top-tier cruise brands in the UK. Our cruise portfolio
            includes everything from sun-soaked Caribbean voyages and scenic Mediterranean getaways
            to breathtaking fjord explorations and cultural river cruises. We offer exotic cruise
            tours and multi-destination sailings that let you experience the world in style. With
            flexible departure ports across the UK, you can set sail on your dream cruise exactly
            the way you want it. All our cruise packages, including flights and accommodations, come
            with full ATOL protection—so in the unlikely event something goes wrong, you won&apos;t
            be left out of pocket or stranded. Just sit back, relax, and sail away knowing you and
            your money are safe. We&apos;re here to help you find your perfect cruise and help you
            experience unforgettable vibes and lifelong memories.
          </Box>
          <Box>
            Why do we call ourselves—and our experiences—“Vibes”? Because we don&apos;t just offer
            cruises; we offer Cruise Vibes. Every voyage with us is designed to deliver nothing but
            good vibes. When you choose us as your cruise specialist, we aim to craft an atmosphere
            you&apos;ll cherish. Your memories are our memories, and your trust means everything to
            us. We take full responsibility for your peace of mind while you&apos;re sailing the
            seas—and we&apos;ll do everything we can to make your cruise smooth, relaxing, and full
            of good vibes.
          </Box>
          <Box>
            Join our VGUK membership and enjoy exclusive access to members-only cruise perks and
            offers. When you&apos;re with us, you&apos;re never just a customer—you&apos;re part of
            our cruise community. We&apos;re here for you whenever you need us, and we want you to
            feel like family. You&apos;re the reason we do what we do. You help us spread cruise
            vibes across the globe!
          </Box>

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, lg: 6 }}>
              <Typography
                color="secondary"
                variant="h5"
                component={"h3"}
                sx={{ marginTop: "1rem" }}
                fontFamily={"Roboto Fallback"}
              >
                Benefits of becoming a member
              </Typography>
              <Box
                sx={{
                  mt: 2,
                  ".MuiListItemIcon-root": {
                    minWidth: "36px !important",
                  },
                }}
              >
                <List
                  sx={{
                    "& .MuiListItem-root": {
                      paddingY: "0px !important",
                      pl: "0px !important",
                    },
                  }}
                >
                  {benefits.map((benefit, index) => (
                    <ListItem
                      key={index}
                      alignItems="flex-start"
                      sx={{ display: "flex", alignItems: "center" }}
                    >
                      <ListItemIcon>
                        <KeyboardDoubleArrowRightIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary={benefit}
                        sx={{
                          "& .MuiTypography-root": {
                            fontSize: "1rem",
                            lineHeight: 1.5,
                          },
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            </Grid>
            <Grid size={{ xs: 12, lg: 6 }}>
              <Typography
                color="secondary"
                variant="h5"
                component={"h3"}
                sx={{ marginTop: "1rem", marginBottom: "1rem" }}
                fontFamily={"Roboto Fallback"}
              >
                VGUK Membership Form
              </Typography>
              {!isOtp && (
                <form onSubmit={handleSubmit(submitEnquiry)}>
                  {!isOtp && (
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12, md: 6, lg: 12 }}>
                        <FormControl variant="outlined" fullWidth>
                          <Controller
                            control={control}
                            name={"firstName"}
                            render={({ field, fieldState: { error, invalid } }) => (
                              <>
                                <TextField
                                  {...field}
                                  label="First Name"
                                  color="secondary"
                                  error={invalid}
                                />
                                <FormHelperText error>{error?.message}</FormHelperText>
                              </>
                            )}
                          />
                        </FormControl>
                      </Grid>

                      <Grid size={{ xs: 12, md: 6, lg: 12 }}>
                        <FormControl variant="outlined" fullWidth>
                          <Controller
                            control={control}
                            name={"lastName"}
                            render={({ field, fieldState: { error, invalid } }) => (
                              <>
                                <TextField
                                  {...field}
                                  label="Last Name"
                                  color="secondary"
                                  error={invalid}
                                />
                                <FormHelperText error>{error?.message}</FormHelperText>
                              </>
                            )}
                          />
                        </FormControl>
                      </Grid>

                      <Grid size={{ xs: 12, md: 6, lg: 12 }}>
                        <FormControl variant="outlined" fullWidth>
                          <Controller
                            control={control}
                            name={"emailAddress"}
                            render={({ field, fieldState: { error, invalid } }) => (
                              <>
                                <TextField
                                  {...field}
                                  label="Email"
                                  color="secondary"
                                  error={invalid}
                                />
                                <FormHelperText error>{error?.message}</FormHelperText>
                              </>
                            )}
                          />
                        </FormControl>
                      </Grid>

                      <Grid size={{ xs: 12, md: 6, lg: 12 }}>
                        <FormControl variant="outlined" fullWidth>
                          <Controller
                            control={control}
                            name={"homeAirport"}
                            render={({ field, fieldState: { error, invalid } }) => (
                              <>
                                <TextField
                                  {...field}
                                  label="Home Airport"
                                  color="secondary"
                                  error={invalid}
                                />
                                <FormHelperText error>{error?.message}</FormHelperText>
                              </>
                            )}
                          />
                        </FormControl>
                      </Grid>

                      <Grid size={{ xs: 12, md: 6, lg: 12 }}>
                        <FormControl variant="outlined" fullWidth>
                          <Controller
                            control={control}
                            name={"favouriteDestination"}
                            render={({ field, fieldState: { error, invalid } }) => (
                              <>
                                <TextField
                                  {...field}
                                  label="Favourite Destination"
                                  color="secondary"
                                  error={invalid}
                                />
                                <FormHelperText error>{error?.message}</FormHelperText>
                              </>
                            )}
                          />
                        </FormControl>
                      </Grid>

                      <Grid size={{ xs: 12, md: 6, lg: 12 }}>
                        <FormControl variant="outlined" fullWidth>
                          <Controller
                            control={control}
                            name={"mobileNumber"}
                            render={({ field, fieldState: { error, invalid } }) => (
                              <>
                                <TextField
                                  {...field}
                                  label="Mobile Number"
                                  color="secondary"
                                  error={invalid}
                                />
                                <FormHelperText error>{error?.message}</FormHelperText>
                              </>
                            )}
                          />
                        </FormControl>
                      </Grid>

                      <Grid size={{ xs: 12, md: 6, lg: 12 }}>
                        <FormControl variant="outlined" fullWidth>
                          <Controller
                            control={control}
                            name={"zipCode"}
                            render={({ field, fieldState: { error, invalid } }) => (
                              <>
                                <TextField
                                  {...field}
                                  label="Zip Code"
                                  color="secondary"
                                  error={invalid}
                                />
                                <FormHelperText error>{error?.message}</FormHelperText>
                              </>
                            )}
                          />
                        </FormControl>
                      </Grid>

                      <Grid size={{ xs: 12, md: 6, lg: 12 }}>
                        <FormControl variant="outlined" fullWidth>
                          <Controller
                            control={control}
                            name={"DOB"}
                            render={({ field, fieldState: { error, invalid } }) => (
                              <>
                                <LocalizationProvider dateAdapter={AdapterMoment}>
                                  <MobileDatePicker
                                    {...field}
                                    label="Date Of Birth"
                                    maxDate={moment().subtract(18, "years")}
                                    minDate={moment().subtract(100, "years")}
                                    openTo="year"
                                    views={["year", "month", "day"]}
                                    slots={{
                                      textField: (params) => (
                                        <TextField {...params} error={invalid} />
                                      ),
                                    }}
                                    value={dobDate}
                                    onChange={(newValue) => {
                                      setDobDate(newValue);
                                    }}
                                  />
                                </LocalizationProvider>
                                <FormHelperText error>{error?.message}</FormHelperText>
                              </>
                            )}
                          />
                        </FormControl>
                      </Grid>

                      <Grid size={{ xs: 12, md: 6, lg: 12 }}>
                        <Button
                          variant="contained"
                          type="submit"
                          disableElevation
                          sx={{
                            color: "#fff",
                            width: { xs: "100%", sm: "200px", lg: "200px" },
                            height: "2.8rem",
                          }}
                        >
                          {" "}
                          {loading ? (
                            <CircularProgress sx={{ color: "#fff" }} size={20} />
                          ) : (
                            <>Submit</>
                          )}
                        </Button>
                      </Grid>
                    </Grid>
                  )}
                </form>
              )}
              {error && <Box sx={{ color: "red", fontSize: "14px", p: "8px" }}>{error}</Box>}
              {isOtp && (
                <OtpNewslater
                  emailAddress={values.emailAddress}
                  firstName={values.firstName}
                  lastName={values.lastName}
                  mobileNumber={values.mobileNumber}
                  status={true}
                  onBack={() => setIsOtp(false)}
                  onSubmit={() => {
                    setIsOtp(false);
                    reset();
                  }}
                />
              )}
            </Grid>
          </Grid>
        </Typography>
        <br />
      </Container>
    </>
  );
};

export default MembersOnly;
